import { useChannelTalk } from '@repo/channel-talk';
import { useUserEventLogger } from '@repo/tracking-service';
import { useRouter } from 'next/router';
import { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';

import ApplyRefundResultNeedConfirmContent from '@/components/tax-refund/apply/result/ApplyRefundResultNeedConfirmContent';
import { ApplyResultDeclareCompleteContent } from '@/components/tax-refund/apply/result/ApplyResultDeclareCompleteContent';
import { ApplyResultImpossibleContent } from '@/components/tax-refund/apply/result/ApplyResultImpossibleContent';
import { ApplyResultPayCompleteContent } from '@/components/tax-refund/apply/result/ApplyResultPayCompleteContent';
import { ApplyResultReceiveCancelContent } from '@/components/tax-refund/apply/result/ApplyResultReceiveCancelContent';
import { ApplyResultRefundCompleteContent } from '@/components/tax-refund/apply/result/ApplyResultRefundCompleteContent';
import { ApplyResultRefundDecisionContent } from '@/components/tax-refund/apply/result/ApplyResultRefundDecisionContent';
import { ApplyResultUnknownContent } from '@/components/tax-refund/apply/result/ApplyResultUnknownContent';
import ApplyResultNoticeSectionView from '@/components/tax-refund/common/view/ApplyResultNoticeSectionView';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { CURRENT_APPLY_RESULT_TERMPLATE_VERSION } from '@/lib/constants/variables';
import { useApplyResultData } from '@/lib/hooks/use-apply-result-data';
import { ApplyResultData, BusinessType } from '@/lib/types/request';

export const ApplyResultContent = ({
  businessType,
  channelTalkLink,
  setChannelTalkLink
}: {
  businessType: BusinessType;
  channelTalkLink: string;
  setChannelTalkLink: Dispatch<SetStateAction<string>>;
}) => {
  const router = useRouter();
  const { openChannelTalk } = useChannelTalk();
  const { sendClickEvent } = useUserEventLogger();

  const { reusultData, applyResultData } = useApplyResultData(businessType);
  const [ApplyResultApplyCompleteCheckContent, setApplyResultApplyCompleteCheckContent] = useState<any>(null);

  const handleAsk = () => {
    sendClickEvent(businessType === 'indis' ? 'indis-result_cx_bottom' : 'corps-result_cx_bottom');
    openChannelTalk(channelTalkLink);
  };

  useEffect(() => {
    let headerChannelTalkLink = SUPPORT_BOT_LINK.default;
    if (applyResultData?.refundStatus === 'RefundFinish') {
      headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.결제완료;
    } else if (applyResultData?.refundStatus === 'RefundImpossible') {
      headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.환급불가;
    } else if (applyResultData?.refundStatus === 'ApplyComplete') {
      if (applyResultData?.refundStage === 'Review') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.신청완료;
      } else if (applyResultData?.refundStage === 'Declare') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.신고완료;
      } else if (applyResultData?.refundStage === 'RefundPreDetermine') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.환급결정;
      } else if (applyResultData?.refundStage === 'RefundDetermine') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.환급결정;
      } else if (applyResultData?.refundStage === 'RefundCompleteA') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.환급완료;
      } else if (applyResultData?.refundStage === 'RefundCompleteB') {
        headerChannelTalkLink = SUPPORT_BOT_LINK.신청결과.결제완료;
      }
    }
    setChannelTalkLink(headerChannelTalkLink);
  }, [applyResultData?.refundStage, applyResultData?.refundStatus, reusultData?.refundStage, reusultData?.refundStatus, setChannelTalkLink]);

  useEffect(() => {
    try {
      const Comp = require(`./template/${CURRENT_APPLY_RESULT_TERMPLATE_VERSION}/ApplyResultApplyCompleteCheckContent`);
      setApplyResultApplyCompleteCheckContent(() => Comp.ApplyResultApplyCompleteCheckContent);
    } catch (e) {
      setApplyResultApplyCompleteCheckContent(null);
    }
  }, []);

  if (!applyResultData) {
    router.push({ pathname: COMMON_PATHS.환급조회목록, query: router.query });
    return null;
  }

  let content: ReactElement = <ApplyResultUnknownContent data={applyResultData as ApplyResultData} />;
  if (applyResultData.refundStatus === 'RefundFinish') {
    content = <ApplyResultPayCompleteContent data={applyResultData as ApplyResultData} />;
  } else if (applyResultData.refundStatus === 'RefundImpossible') {
    content = <ApplyResultImpossibleContent data={applyResultData as ApplyResultData} />;
  } else if (applyResultData.refundStatus === 'ApplyComplete') {
    if (applyResultData.cancelStage === 'Canceling') {
      content = <ApplyResultReceiveCancelContent data={applyResultData as ApplyResultData} />;
    } else if (applyResultData.refundStage === 'Review') {
      content = ApplyResultApplyCompleteCheckContent ? <ApplyResultApplyCompleteCheckContent data={applyResultData} /> : <></>;
    } else if (applyResultData.refundStage === 'Declare') {
      content = <ApplyResultDeclareCompleteContent data={applyResultData as ApplyResultData} />;
    } else if (applyResultData.refundStage === 'RefundPreDetermine') {
      content = <ApplyRefundResultNeedConfirmContent data={applyResultData as ApplyResultData} />;
    } else if (applyResultData.refundStage === 'RefundDetermine') {
      content = <ApplyResultRefundDecisionContent data={applyResultData as ApplyResultData} />;
    } else if (applyResultData.refundStage === 'RefundCompleteA') {
      content = <ApplyResultRefundCompleteContent data={applyResultData as ApplyResultData} channelTalkLink={channelTalkLink} />;
    } else if (applyResultData.refundStage === 'RefundCompleteB') {
      content = <ApplyResultRefundCompleteContent data={applyResultData as ApplyResultData} channelTalkLink={channelTalkLink} />;
    }
  }

  return (
    <>
      {content}
      <ApplyResultNoticeSectionView handleAsk={handleAsk} />
      {reusultData?.cancelStage === 'Canceling' && <div className="bg-gray-50 pb-20" />}
    </>
  );
};
